import i18n from "../i18n";
import { getDeviceFamilyFromId } from "../helpers/DevicePresence";

export const devicesOptions = ["Andel_348518515ec8"];

//A measure with `families` only applies to devices of those families. A measure without it applies
//to every device. FS2 units do not write relay_status or the battery measures.
const CC_ONLY = ["CC"];
const FS2_ONLY = ["FS2"];

//Measures written by FS2 units only. flow2_* is only written when valve 2 is enabled on the unit.
const fs2Measures = [
	{ value: "flow1_rate_lpm", isBoolean: false },
	{ value: "flow1_volume_10m_l", isBoolean: false },
	{ value: "flow1_volume_1h_l", isBoolean: false },
	{ value: "flow2_rate_lpm", isBoolean: false },
	{ value: "flow2_volume_10m_l", isBoolean: false },
	{ value: "flow2_volume_1h_l", isBoolean: false },
	{ value: "temp_ambient_c", isBoolean: false },
	{ value: "temp_internal_c", isBoolean: false },
	{ value: "valve1_status", isBoolean: true },
	{ value: "valve2_status", isBoolean: true },
	{ value: "output_relay1_status", isBoolean: true },
	{ value: "output_relay2_status", isBoolean: true },
	{ value: "alarm_active", isBoolean: true },
	{ value: "alarm_override_active", isBoolean: true },
	{ value: "sensor1_fault", isBoolean: true },
	{ value: "uptime_s", isBoolean: false },
].map((measure) => ({ ...measure, label: i18n.t(`report.measure.${measure.value}`), families: FS2_ONLY }));

export const measureOptions = [
	{
		value: "sensor1_status",
		label: i18n.t("report.measure.sensor1_status"),
		aggTypes: ["SUM", "COUNT", "AVG"],
		isBoolean: true,
	},
	{
		value: "sensor1_resistance",
		label: i18n.t("report.measure.sensor1_resistance"),
		aggTypes: ["SUM", "AVG", "MIN", "MAX"],
		isBoolean: false,
	},
	{
		value: "relay_status",
		label: i18n.t("report.measure.relay_status"),
		aggTypes: ["SUM", "COUNT", "AVG"],
		isBoolean: true,
		families: CC_ONLY,
	},
	{
		value: "battery_percentage",
		label: i18n.t("report.measure.battery_percentage"),
		aggTypes: ["SUM", "AVG", "MIN", "MAX"],
		isBoolean: false,
		families: CC_ONLY,
	},
	{
		value: "battery_charging",
		label: i18n.t("report.measure.battery_charging"),
		aggTypes: ["SUM", "AVG"],
		isBoolean: true,
		families: CC_ONLY,
	},
	{
		value: "dcPower_status",
		label: i18n.t("report.measure.dcPower_status"),
		aggTypes: ["SUM", "AVG"],
		isBoolean: true,
	},
	{
		value: "dcPower_voltage",
		label: i18n.t("report.measure.dcPower_voltage"),
		aggTypes: ["SUM", "AVG", "MIN", "MAX"],
		isBoolean: false,
	},
	{
		value: "wifi_rssi",
		label: i18n.t("report.measure.wifi_rssi"),
		aggTypes: ["SUM", "AVG", "MIN", "MAX"],
		isBoolean: false,
	},
	...fs2Measures.map((measure) => ({
		...measure,
		aggTypes: measure.isBoolean ? ["SUM", "AVG"] : ["SUM", "AVG", "MIN", "MAX"],
	})),
];

export const measureOptionsInstant = [
	{
		value: "sensor1_status",
		label: i18n.t("report.measure.sensor1_status"),
		type: "INSTANT",
		isBoolean: true,
	},
	{
		value: "sensor1_resistance",
		label: i18n.t("report.measure.sensor1_resistance"),
		type: "INSTANT",
		isBoolean: false,
	},
	{
		value: "relay_status",
		label: i18n.t("report.measure.relay_status"),
		type: "INSTANT",
		isBoolean: true,
		families: CC_ONLY,
	},
	{
		value: "battery_percentage",
		label: i18n.t("report.measure.battery_percentage"),
		type: "INSTANT",
		isBoolean: false,
		families: CC_ONLY,
	},
	{
		value: "battery_charging",
		label: i18n.t("report.measure.battery_charging"),
		type: "INSTANT",
		isBoolean: true,
		families: CC_ONLY,
	},
	{
		value: "dcPower_status",
		label: i18n.t("report.measure.dcPower_status"),
		type: "INSTANT",
		isBoolean: true,
	},
	{
		value: "dcPower_voltage",
		label: i18n.t("report.measure.dcPower_voltage"),
		type: "INSTANT",
		isBoolean: false,
	},
	{
		value: "wifi_rssi",
		label: i18n.t("report.measure.wifi_rssi"),
		type: "INSTANT",
		isBoolean: false,
	},
	...fs2Measures.map((measure) => ({ ...measure, type: "INSTANT" })),
	{
		value: "firmware_version",
		label: i18n.t("report.measure.firmware_version"),
		type: "INSTANT_DIMENSION",
		isBoolean: false,
	},
	{
		value: "hardware_version",
		label: i18n.t("report.measure.hardware_version"),
		type: "INSTANT_DIMENSION",
		isBoolean: false,
	},
	{
		value: "inputDevice",
		label: i18n.t("report.measure.inputDevice"),
		type: "INSTANT_DIMENSION",
		isBoolean: false,
	},
	{
		value: "outputDevice",
		label: i18n.t("report.measure.outputDevice"),
		type: "INSTANT_DIMENSION",
		isBoolean: false,
	},
];

//The measures that apply to the devices a report is run for. FS2 units get the FS2 measures and
//not the CloudConnect only ones, CloudConnect devices keep their list, and a mixed selection gets
//both. A device with an unknown id prefix counts as CloudConnect. No devices gives every measure.
export const getMeasureOptionsForDevices = (options, devices) => {
	const families = new Set(
		(Array.isArray(devices) ? devices : [])
			.filter((device) => device?.id)
			.map((device) => getDeviceFamilyFromId(device.id) ?? "CC")
	);
	if (families.size === 0) return options;

	return options.filter((option) => !option.families || option.families.some((family) => families.has(family)));
};

export const aggregateOptions = [
	// { value: "SUM", label: "Sum" },
	// { value: "COUNT", label: "Count" },
	{ value: "AVG", label: i18n.t("report.aggregate.AVG") },
	{ value: "MIN", label: i18n.t("report.aggregate.MIN") },
	{ value: "MAX", label: i18n.t("report.aggregate.MAX") },
];

export const intervalOptions = [
	{ value: 0, label: "Custom" },
	{ value: -1, label: "All" },
	{ value: 60, label: "1 min" },
	{ value: 600, label: "10 mins" },
	{ value: 1800, label: "30 mins" },
	{ value: 3600, label: "1 hour" },
	{ value: 10800, label: "3 hours" },
	{ value: 21600, label: "6 hours" },
	{ value: 43200, label: "12 hours" },
	{ value: 86400, label: "24 hours" },
];
